import { type Infer, v } from 'convex/values'

import { dayShareUrl } from './dayShare'
import type { FinishId } from './dotFinishes'
import type { LevelId } from './productivity'
import { HABIT_LEVELS, habitLevel } from './progression'
import { withShareUtm } from './shareUtm'

// Instagram Story share cards: pure data builders, options and URL tokens shared
// by the data query, the PNG renderer and both clients.

export const CARD_TYPES = [
  'streak',
  'level',
  'goal',
  'wrap',
  'week',
  'best',
  'progress',
  'day',
  'productivity',
  'journey',
] as const
export type CardType = (typeof CARD_TYPES)[number]
export const cardTypeValidator = v.union(
  v.literal('streak'),
  v.literal('level'),
  v.literal('goal'),
  v.literal('wrap'),
  v.literal('week'),
  v.literal('best'),
  v.literal('progress'),
  v.literal('day'),
  v.literal('productivity'),
  v.literal('journey'),
)

export const CARD_STYLES: Record<CardType, readonly string[]> = {
  streak: ['dots', 'calendar'],
  level: ['default'],
  goal: ['default'],
  wrap: ['night', 'poster'],
  week: ['default'],
  best: ['default'],
  progress: ['default'],
  day: ['default'],
  productivity: ['default'],
  journey: ['default'],
}

export const CARD_W = 1080
export const CARD_H = 1920
// Picker thumbnail and in-sheet preview widths; shares use the full CARD_W.
export const THUMB_W = 180
export const PREVIEW_W = 540

export const CARD_THEMES = ['dark', 'light'] as const
export type CardTheme = (typeof CARD_THEMES)[number]

export const ACCENTS = {
  pink: '#ec6489',
  gold: '#ffd700',
  orange: '#ff6b35',
  green: '#a8ff3e',
  cyan: '#00d4ff',
  purple: '#2dd4bf',
} as const
export type AccentName = keyof typeof ACCENTS | 'category'
export const ACCENT_NAMES = ['category', ...Object.keys(ACCENTS)] as AccentName[]

export const CATEGORY_COLORS: Record<string, string> = {
  health: '#FF6452',
  career: '#3DA9FC',
  mind: '#a8ff3e',
  finance: '#FFB020',
  relationships: '#FF5C8A',
  creative: '#2DD4BF',
}

// Text/line shade of each fill on the light (paper) ground, where the bright
// fills fail contrast.
export const PAPER_INK: Record<string, string> = {
  '#ec6489': '#c2335d',
  '#ffd700': '#9a7400',
  '#ff6b35': '#d4470f',
  '#a8ff3e': '#4d7c0f',
  '#00d4ff': '#0284a8',
  '#2dd4bf': '#0f766e',
  '#b87aff': '#0f766e',
  '#FF6452': '#d2402f',
  '#3DA9FC': '#1f6fb8',
  '#FFB020': '#b45309',
  '#FF5C8A': '#d6336c',
  '#2DD4BF': '#0f766e',
  '#A66BFF': '#0f766e',
}

// Cards with no single category default to a fixed accent (gold is for levels).
const TYPE_ACCENT: Partial<Record<CardType, string>> = {
  level: ACCENTS.gold,
  wrap: ACCENTS.pink,
  week: ACCENTS.pink,
  productivity: ACCENTS.pink,
}

export function accentColor(type: CardType, accent: string, category?: string | null): string {
  if (accent in ACCENTS) return ACCENTS[accent as keyof typeof ACCENTS]
  // The day card's "category" is the app accent; pink when there is none.
  if (type === 'day') return CATEGORY_COLORS[category ?? ''] ?? ACCENTS.pink
  return TYPE_ACCENT[type] ?? CATEGORY_COLORS[category ?? ''] ?? ACCENTS.green
}

export const cardOptionsValidator = v.object({
  style: v.optional(v.string()),
  theme: v.optional(v.union(v.literal('dark'), v.literal('light'))),
  accent: v.optional(v.string()),
})
export type CardOptions = Infer<typeof cardOptionsValidator>

export type ResolvedOptions = { style: string; theme: CardTheme; accent: AccentName }

export function resolveOptions(type: CardType, o: Partial<CardOptions> | null | undefined) {
  const styles = CARD_STYLES[type]
  return {
    style: o?.style && styles.includes(o.style) ? o.style : styles[0],
    theme: o?.theme === 'light' ? 'light' : 'dark',
    accent: ACCENT_NAMES.includes(o?.accent as AccentName) ? (o!.accent as AccentName) : 'category',
  } satisfies ResolvedOptions
}

// ─── Signed image URLs ────────────────────────────────────────────────────────
// The image route serves private data with no session, so access rides on a
// short-lived HMAC token naming the owner, card and subject.

export const TOKEN_TTL_MS = 6 * 60 * 60 * 1000

export type CardTokenPayload = { u: string; k: CardType; r: string; e: number }

const enc = new TextEncoder()
const b64url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
const fromB64url = (s: string) =>
  Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))

async function hmac(secret: string, data: string) {
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  return new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(data)))
}

export async function signCardToken(payload: CardTokenPayload, secret: string) {
  const body = b64url(enc.encode(JSON.stringify(payload)))
  return `${body}.${b64url(await hmac(secret, body))}`
}

export async function verifyCardToken(
  token: string,
  secret: string,
  now: number,
): Promise<CardTokenPayload | null> {
  const [body, sig] = token.split('.')
  if (!body || !sig) return null
  const expected = b64url(await hmac(secret, body))
  if (expected.length !== sig.length) return null
  let diff = 0
  for (let i = 0; i < sig.length; i++) diff |= expected.charCodeAt(i) ^ sig.charCodeAt(i)
  if (diff) return null
  try {
    const p = JSON.parse(new TextDecoder().decode(fromB64url(body))) as CardTokenPayload
    if (typeof p.u !== 'string' || typeof p.r !== 'string' || typeof p.e !== 'number') return null
    if (!CARD_TYPES.includes(p.k) || p.e < now) return null
    return p
  } catch {
    return null
  }
}

// ─── Day keys ─────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000
export const addDays = (key: string, n: number) =>
  new Date(Date.parse(`${key}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10)
export const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS)
export const mondayIndex = (key: string) => (new Date(`${key}T12:00:00Z`).getUTCDay() + 6) % 7
export const weekStart = (key: string) => addDays(key, -mondayIndex(key))
export const daysInMonth = (month: string) => {
  const [y, m] = month.split('-').map(Number)
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}
export const prevMonth = (month: string) => {
  const [y, m] = month.split('-').map(Number)
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`
}

// ─── Card data ────────────────────────────────────────────────────────────────

export type Cadence = 'day' | 'week' | 'month'

// The habit's custom done-dot (Pro). Icon and image carry `src` (a data URI)
// once the renderer has fetched them; without it they fall back to plain dots.
export type CardDot =
  | { kind: 'emoji'; emoji: string }
  | { kind: 'smiley' }
  | { kind: 'icon'; icon: string; src?: string }
  | { kind: 'image'; url: string; src?: string }

// A day's habit photo, only present when the user turned on "Include photos";
// `src` is the fetched data URI.
export type CardPhoto = { date: string; url: string; src?: string }

export type StreakCard = {
  type: 'streak'
  dot?: CardDot
  photos?: CardPhoto[]
  // Earned finish (lib/dotFinishes): done dots drawn in its metal.
  finish?: FinishId
  // The all-habits daily streak rather than one habit's.
  all?: boolean
  title: string
  category: string
  streak: number
  best: number
  cadence: Cadence
  start: string | null
  today: string
  // Set after a miss (streak 0, or 1 right after a gap): the last scheduled
  // days, oldest first.
  recent?: { date: string; done: boolean }[]
}

export type LevelCard = {
  type: 'level'
  title: string
  category: string
  level: number
  name: string
  total: number
  next: number | null
  nextName: string | null
}

export type GoalCard = {
  type: 'goal'
  title: string
  category: string
  created: string
  completed: string
  days: number
  milestones: { title: string; done: boolean; tasks: boolean[] }[]
  taskCount: number
  tasksDone: number
}

// Before/after: a still (photo only once screened 'ok') and/or the note, per end.
export type JourneyMoment = {
  label: 'start' | 'now' | 'done'
  url?: string
  src?: string
  note?: string
}
export type JourneyCard = {
  type: 'journey'
  title: string
  category: string
  days: number
  before: JourneyMoment | null
  after: JourneyMoment | null
  photos?: CardPhoto[]
  streak: number
  checkIns: number
  tasksDone: number
}

export type WrapCard = {
  type: 'wrap'
  month: string
  photos?: CardPhoto[]
  daysInMonth: number
  activeDays: number
  // Heat level 0–4 per day of the month.
  heat: number[]
  checkIns: number
  habits: number
  top: { title: string; count: number } | null
  longest: { title: string; days: number } | null
  goals: { title: string; on: string }[]
  categories: { category: string; count: number }[]
}

export type WeekCard = {
  type: 'week'
  // Monday and Sunday of the (local) week.
  weekStart: string
  weekEnd: string
  // Heat level 0–4 per day, Monday first.
  heat: number[]
  activeDays: number
  checkIns: number
  habits: number
  streaksAlive: number
  top: { title: string; count: number } | null
}

export type BestCard = {
  type: 'best'
  title: string
  category: string
  unit: string
  best: number
  bestOn: string
  // Per-session (day) bests in the window, oldest first.
  series: { date: string; value: number }[]
  gain: number
  // Target in effect at the window start, then each raise inside it.
  targets: { on: string; to: number }[]
  target: number | null
  today: string
}

export type ProgressCard = {
  type: 'progress'
  title: string
  category: string
  unit: string
  firstWeek: string
  firstAvg: number
  lastAvg: number
  weeks: number
  weekly: { week: string; avg: number }[]
  multiplier: number
  today: string
}

// Today, as gt.today/<handle> shows it: null parts are hidden by the day-share
// toggles. `category` carries the app accent (by its category colour).
export type DayCard = {
  type: 'day'
  name: string
  category: string
  date: string
  streak: number | null
  habits:
    | {
        title: string
        done: boolean
        measure: { total: number; target: number; unit: string } | null
        // The habit's own look for its done marker, as on its streak card.
        dot?: CardDot
        finish?: FinishId
      }[]
    | null
}

// Productivity level for a Mon–Sun week (lib/productivity).
export type ProductivityCard = {
  type: 'productivity'
  weekStart: string
  weekEnd: string
  score: number
  level: LevelId
  // 95+ this week but not last: held at Insane.
  legendaryLocked: boolean
  next: { level: LevelId; points: number } | null
  best: boolean
  // Weeks compared for a best (current included).
  bestCompared: number
  // Week start of the last week that matched it, when 3+ weeks back.
  bestSince: string | null
}

export type CardData =
  | ProductivityCard
  | DayCard
  | StreakCard
  | LevelCard
  | GoalCard
  | WrapCard
  | WeekCard
  | BestCard
  | ProgressCard
  | JourneyCard

export const cadenceOf = (period: string): Cadence =>
  period === 'weekly' ? 'week' : period === 'monthly' ? 'month' : 'day'

// First day of the current run: walk back over the year codes (oldest first,
// last = today) until `streak` hit periods are counted.
export function streakStart(
  year: string,
  today: string,
  streak: number,
  cadence: Cadence,
): string | null {
  if (streak <= 0) return null
  const periodOf = (k: string) =>
    cadence === 'month' ? k.slice(0, 7) : cadence === 'week' ? weekStart(k) : k
  const hit = new Set<string>()
  let start: string | null = null
  for (let i = year.length - 1; i >= 0; i--) {
    if (year[i] !== 'd') continue
    const k = addDays(today, i - year.length + 1)
    const p = periodOf(k)
    if (!hit.has(p)) {
      if (hit.size === streak) break
      hit.add(p)
    }
    start = k
  }
  return start
}

export const RECENT_DAYS = 14

// Last scheduled days from the year codes (oldest first, last = today); today
// counts only once done.
export function recentDays(year: string, today: string, n = RECENT_DAYS) {
  const out: { date: string; done: boolean }[] = []
  for (let i = year.length - 1; i >= 0 && out.length < n; i--) {
    const c = year[i]
    if (c === 'd' || c === 'm')
      out.push({ date: addDays(today, i - year.length + 1), done: c === 'd' })
  }
  return out.reverse()
}

// Comeback version of the streak cards: nothing running, or a fresh 1 after a miss.
export const isComeback = (streak: number, recent: { done: boolean }[]) =>
  streak === 0 ? recent.length > 0 : streak === 1 && recent.some((r) => !r.done)

// Whether the picker may open on this card; else it opens on this month or the level.
export const opensWell = (d: CardData) =>
  d.type === 'streak' ? d.streak > 0 : d.type === 'wrap' ? d.activeDays > 0 : true

const OPEN_FALLBACK: CardType[] = ['wrap', 'level']

// Card key the picker opens on: the primary unless it opens poorly, then the first
// minted fallback that opens well. Undefined while any of them is still minting.
export function openingKey<T extends { type: CardType; ref: string }>(
  primary: string,
  targets: T[],
  cards: Record<string, { opens?: boolean } | null>,
): string | undefined {
  const p = cards[primary]
  if (!p) return undefined
  if (p.opens !== false) return primary
  const keys = OPEN_FALLBACK.flatMap((t) =>
    targets.filter((x) => x.type === t).map((x) => `${x.type}:${x.ref}`),
  ).filter((k) => k !== primary)
  if (keys.some((k) => !(k in cards))) return undefined
  return keys.find((k) => cards[k]?.opens) ?? primary
}

export function levelCard(title: string, category: string, total: number): LevelCard {
  const l = habitLevel(total)
  return {
    type: 'level',
    title,
    category,
    level: l.level,
    name: l.name,
    total,
    next: l.next,
    nextName: HABIT_LEVELS[l.level]?.name ?? null,
  }
}

export type WrapInput = {
  month: string
  // Streak-counting completions in (and around) the month; habitId set for habits.
  completions: { date: string; habitId: string | null }[]
  habits: Map<string, { title: string; category: string }>
  goals: { title: string; on: string }[]
}

export function monthWrap(w: WrapInput): WrapCard {
  const n = daysInMonth(w.month)
  const inMonth = w.completions.filter((c) => c.date.startsWith(`${w.month}-`))
  const active = new Set(inMonth.map((c) => c.date))
  const perDay = new Map<string, Set<string>>()
  const perHabit = new Map<string, Set<string>>()
  for (const c of inMonth) {
    if (!c.habitId || !w.habits.has(c.habitId)) continue
    if (!perDay.has(c.date)) perDay.set(c.date, new Set())
    perDay.get(c.date)!.add(c.habitId)
    if (!perHabit.has(c.habitId)) perHabit.set(c.habitId, new Set())
    perHabit.get(c.habitId)!.add(c.date)
  }
  const dayKeys = Array.from(
    { length: n },
    (_, i) => `${w.month}-${String(i + 1).padStart(2, '0')}`,
  )
  const counts = dayKeys.map((k) => (perDay.get(k)?.size ?? 0) || (active.has(k) ? 1 : 0))
  const peak = Math.max(1, ...counts)
  const heat = counts.map((c) => (c ? Math.min(4, Math.max(1, Math.ceil((c / peak) * 4))) : 0))

  let top: WrapCard['top'] = null
  let longest: WrapCard['longest'] = null
  const cats = new Map<string, number>()
  let checkIns = 0
  for (const [id, days] of perHabit) {
    const h = w.habits.get(id)!
    checkIns += days.size
    cats.set(h.category, (cats.get(h.category) ?? 0) + days.size)
    if (!top || days.size > top.count) top = { title: h.title, count: days.size }
    let run = 0
    let best = 0
    for (const k of dayKeys) {
      run = days.has(k) ? run + 1 : 0
      best = Math.max(best, run)
    }
    if (!longest || best > longest.days) longest = { title: h.title, days: best }
  }
  return {
    type: 'wrap',
    month: w.month,
    daysInMonth: n,
    activeDays: active.size,
    heat,
    checkIns,
    habits: perHabit.size,
    top,
    longest: longest && longest.days > 1 ? longest : null,
    goals: w.goals
      .filter((g) => g.on.startsWith(`${w.month}-`))
      .sort((a, b) => (a.on < b.on ? -1 : 1)),
    categories: [...cats]
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3),
  }
}

export const isWeekStart = (key: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(key) &&
  !Number.isNaN(Date.parse(`${key}T00:00:00Z`)) &&
  addDays(key, 0) === key &&
  mondayIndex(key) === 0

// Same counting as monthWrap: activity days include focus tasks, check-ins are
// distinct habit-days.
export function weekRecap(w: {
  weekStart: string
  completions: { date: string; habitId: string | null }[]
  habits: Map<string, { title: string; category: string }>
  streaksAlive: number
}): WeekCard {
  const keys = Array.from({ length: 7 }, (_, i) => addDays(w.weekStart, i))
  const inWeek = w.completions.filter((c) => keys.includes(c.date))
  const active = new Set(inWeek.map((c) => c.date))
  const perDay = new Map<string, Set<string>>()
  const perHabit = new Map<string, Set<string>>()
  for (const c of inWeek) {
    if (!c.habitId || !w.habits.has(c.habitId)) continue
    if (!perDay.has(c.date)) perDay.set(c.date, new Set())
    perDay.get(c.date)!.add(c.habitId)
    if (!perHabit.has(c.habitId)) perHabit.set(c.habitId, new Set())
    perHabit.get(c.habitId)!.add(c.date)
  }
  const counts = keys.map((k) => (perDay.get(k)?.size ?? 0) || (active.has(k) ? 1 : 0))
  const peak = Math.max(1, ...counts)
  let top: WeekCard['top'] = null
  let checkIns = 0
  for (const [id, days] of perHabit) {
    checkIns += days.size
    if (!top || days.size > top.count) top = { title: w.habits.get(id)!.title, count: days.size }
  }
  return {
    type: 'week',
    weekStart: w.weekStart,
    weekEnd: keys[6],
    heat: counts.map((c) => (c ? Math.min(4, Math.max(1, Math.ceil((c / peak) * 4))) : 0)),
    activeDays: active.size,
    checkIns,
    habits: perHabit.size,
    streaksAlive: w.streaksAlive,
    top,
  }
}

// Per-day amounts: the best entry for 'max' habits, else the day's sum.
export function dailyAmounts(logs: { date: string; amount: number }[], max: boolean) {
  const days = new Map<string, number>()
  for (const l of logs) {
    const cur = days.get(l.date)
    days.set(l.date, cur === undefined ? l.amount : max ? Math.max(cur, l.amount) : cur + l.amount)
  }
  return [...days].sort((a, b) => (a[0] < b[0] ? -1 : 1))
}

const MAX_SESSIONS = 16

export type BestOf = { best: number; bestOn: string; before: number | null }

// All-time best session and the best before the day it was first reached.
export function bestOf(days: [string, number][]): BestOf | null {
  let best = null as BestOf | null
  for (const [date, value] of days) {
    if (!best || value > best.best) best = { best: value, bestOn: date, before: best?.best ?? null }
  }
  return best
}

export function personalBest(input: {
  logs: { date: string; amount: number }[]
  history: { on: string; from: number; to: number }[]
  target: number | null
  today: string
}): Omit<BestCard, 'title' | 'category' | 'unit'> | null {
  const days = dailyAmounts(input.logs, true)
  const b = bestOf(days)
  if (!b) return null
  const series = days.slice(-MAX_SESSIONS).map(([date, value]) => ({ date, value }))
  const start = series[0].date
  const hist = [...input.history].sort((a, c) => (a.on < c.on ? -1 : 1))
  const atStart =
    [...hist].reverse().find((h) => h.on <= start)?.to ?? hist[0]?.from ?? input.target
  const targets = [
    ...(atStart !== null && atStart !== undefined ? [{ on: start, to: atStart }] : []),
    ...hist.filter((h) => h.on > start && h.on <= input.today).map((h) => ({ on: h.on, to: h.to })),
  ]
  return {
    type: 'best',
    best: b.best,
    bestOn: b.bestOn,
    series,
    gain: Math.max(0, b.best - series[0].value),
    targets,
    target: input.target,
    today: input.today,
  }
}

export const BEFORE_AFTER_MIN_WEEKS = 4
export const BEFORE_AFTER_MIN_MULTIPLIER = 2
const MAX_WEEKS = 26

// Weekly average per logged day; eligible once the latest week is 2× the first,
// 4+ weeks later.
export function beforeAfter(input: {
  logs: { date: string; amount: number }[]
  max: boolean
  today: string
}): (Omit<ProgressCard, 'title' | 'category' | 'unit'> & { eligible: boolean }) | null {
  const days = dailyAmounts(input.logs, input.max)
  if (!days.length) return null
  const weeks = new Map<string, number[]>()
  for (const [d, a] of days) {
    const w = weekStart(d)
    if (!weeks.has(w)) weeks.set(w, [])
    weeks.get(w)!.push(a)
  }
  const weekly = [...weeks]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([week, xs]) => ({
      week,
      avg: Math.round((xs.reduce((s, x) => s + x, 0) / xs.length) * 10) / 10,
    }))
  const first = weekly[0]
  const last = weekly[weekly.length - 1]
  const span = Math.round(daysBetween(first.week, last.week) / 7)
  const multiplier = first.avg > 0 ? Math.round((last.avg / first.avg) * 10) / 10 : 0
  return {
    type: 'progress',
    firstWeek: first.week,
    firstAvg: first.avg,
    lastAvg: last.avg,
    weeks: span,
    weekly: [first, ...weekly.slice(1).slice(-(MAX_WEEKS - 1))],
    multiplier,
    today: input.today,
    eligible:
      span >= BEFORE_AFTER_MIN_WEEKS &&
      first.avg > 0 &&
      last.avg >= first.avg * BEFORE_AFTER_MIN_MULTIPLIER,
  }
}

// Footer + link sticker URL.
export function cardLink(
  type: CardType,
  o: {
    handle: string | null
    dayShareOn: boolean
    goalId?: string
    goalTitle?: string
    journeyUrl?: string
  },
) {
  if (type === 'journey' && o.journeyUrl) return withShareUtm(o.journeyUrl, 'share_card')
  if (type === 'goal' && o.goalId) {
    const slug = (o.goalTitle ?? '')
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
    return withShareUtm(`https://goaltree.ai/share/${slug || 'goal'}-${o.goalId}`, 'share_card')
  }
  if (o.handle && o.dayShareOn) return withShareUtm(`https://gt.today/${o.handle}`, 'share_card')
  return withShareUtm(o.handle ? dayShareUrl(o.handle) : 'https://goaltree.ai', 'share_card')
}
